import { z } from 'zod';
import { IsoDateTime, Uuid } from '../primitives.js';

/**
 * WS-13 Feature 1 — REVIEWS (LRU-05). A review is a salon-PUBLIC artifact (it renders on the public
 * booking page + the JSON-LD aggregateRating), gated by a single-use token bound to a COMPLETED
 * appointment (verified-visit reviews are low-spam and SEO-safe). It is NOT renter-sealed.
 *
 * PII discipline: a review carries free text + an author NAME. The author name is DENORMALISED at insert
 * (e.g. "Sarah M.") so that a hidden/sealed client's REAL name never renders. The PUBLIC DTOs below
 * (PublicReview / PublicReviewSummary) carry NO internal ids and read ONLY the denormalised name — they
 * never expose a clientId/appointmentId/staffId, and never join back to the clients table.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/**
 * Review lifecycle. 'pending' = awaiting moderation (manual mode); 'published' = live (rendered
 * publicly + counted in the aggregate); 'hidden' = operator-suppressed (auto mode lets the operator
 * hide a published review).
 */
export const ReviewStatus = z.enum(['pending', 'published', 'hidden']);
export type ReviewStatus = z.infer<typeof ReviewStatus>;

/**
 * Per-salon moderation mode. 'auto' = new reviews start 'published' then the operator can hide (the
 * default — low-spam token-gated reviews, maximises SEO); 'manual' = new reviews start 'pending'.
 */
export const ReviewModeration = z.enum(['auto', 'manual']);
export type ReviewModeration = z.infer<typeof ReviewModeration>;

// ── Review (operator-facing entity — full ids) ─────────────────────────────────

/**
 * The operator's view of a review (moderation list / per-stylist breakdown). Carries the salon-internal
 * ids the console needs to moderate; NEVER served on the public surface.
 */
export const Review = z
  .object({
    id: Uuid,
    salonId: Uuid,
    appointmentId: Uuid.nullable(),
    clientId: Uuid.nullable(),
    staffId: Uuid.nullable().describe(
      'the stylist the review is attributed to (per-stylist aggregate)',
    ),
    rating: z.int().min(1).max(5),
    body: z.string().nullable(),
    authorDisplayName: z
      .string()
      .describe(
        'denormalised at insert (e.g. "Sarah M.") — a sealed/hidden client name never renders',
      ),
    status: ReviewStatus,
    salonReply: z.string().nullable(),
    salonRepliedAt: IsoDateTime.nullable(),
    submittedAt: IsoDateTime.nullable(),
    publishedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Review' });
export type Review = z.infer<typeof Review>;

// ── Public read views (NO internal ids — customer-facing only) ─────────────────

/**
 * A single published review on the PUBLIC surface (the booking page + JSON-LD review list). Deliberately
 * omits ALL salon-internal ids (id, clientId, appointmentId, staffId) — only customer-facing fields. The
 * author name is the denormalised authorDisplayName, never the client's real name.
 */
export const PublicReview = z
  .object({
    authorDisplayName: z.string(),
    rating: z.int().min(1).max(5),
    body: z.string().nullable(),
    publishedAt: IsoDateTime.nullable(),
    serviceName: z.string().nullable().optional(),
    staffDisplayName: z.string().nullable().optional(),
  })
  .meta({ id: 'PublicReview' });
export type PublicReview = z.infer<typeof PublicReview>;

/**
 * The public aggregate for a salon (drives the booking-page JSON-LD aggregateRating + the bounded review
 * list). averageRating is a decimal 0..5; reviewCount is the published count. No internal ids anywhere.
 */
export const PublicReviewSummary = z
  .object({
    averageRating: z.number().min(0).max(5),
    reviewCount: z.int().min(0),
    reviews: z.array(PublicReview),
  })
  .meta({ id: 'PublicReviewSummary' });
export type PublicReviewSummary = z.infer<typeof PublicReviewSummary>;

/**
 * What a review-request token resolves to (the prefilled public submit form): the salon + visit context
 * a client sees before writing their review. No internal ids — only display strings.
 */
export const PublicReviewRequest = z
  .object({
    salonName: z.string(),
    serviceName: z.string().nullable(),
    staffName: z.string().nullable(),
  })
  .meta({ id: 'PublicReviewRequest' });
export type PublicReviewRequest = z.infer<typeof PublicReviewRequest>;

// ── Requests ─────────────────────────────────────────────────────────────────

/** Moderate a review: publish/hide it, optionally attaching/replacing the salon reply. Manager+. */
export const ModerateReview = z
  .object({
    status: ReviewStatus,
    salonReply: z.string().trim().min(1).max(2000).optional(),
  })
  .meta({ id: 'ModerateReview' });
export type ModerateReview = z.infer<typeof ModerateReview>;

/** Attach (or replace) the salon's public reply to a review. Manager+. */
export const ReplyToReview = z
  .object({
    salonReply: z.string().trim().min(1).max(2000),
  })
  .meta({ id: 'ReplyToReview' });
export type ReplyToReview = z.infer<typeof ReplyToReview>;

/**
 * Mint a review-request token for a COMPLETED appointment and enqueue the send. Idempotent on
 * (salon_id, appointmentId): a re-request returns the existing token row. Manager+.
 */
export const RequestReview = z
  .object({
    appointmentId: Uuid,
    channel: z.enum(['sms', 'email', 'whatsapp']).optional(),
  })
  .meta({ id: 'RequestReview' });
export type RequestReview = z.infer<typeof RequestReview>;

/** The per-salon review settings (moderation mode + auto-request opt-in). */
export const ReviewSettings = z
  .object({
    moderation: ReviewModeration,
    autoRequestEnabled: z.boolean(),
    autoRequestDelayMinutes: z
      .int()
      .min(0)
      .max(43_200)
      .describe('delay after completion, in minutes'),
  })
  .meta({ id: 'ReviewSettings' });
export type ReviewSettings = z.infer<typeof ReviewSettings>;

/** Upsert the per-salon review settings (all fields optional — partial update). Manager+. */
export const UpsertReviewSettings = z
  .object({
    moderation: ReviewModeration.optional(),
    autoRequestEnabled: z.boolean().optional(),
    autoRequestDelayMinutes: z.int().min(0).max(43_200).optional(),
  })
  .meta({ id: 'UpsertReviewSettings' });
export type UpsertReviewSettings = z.infer<typeof UpsertReviewSettings>;

/** The public submission a client posts against a valid (unused, unexpired) review-request token. */
export const CreateReviewSubmission = z
  .object({
    rating: z.int().min(1).max(5),
    body: z.string().trim().max(4000).optional(),
  })
  .meta({ id: 'CreateReviewSubmission' });
export type CreateReviewSubmission = z.infer<typeof CreateReviewSubmission>;

// ── Query schemas (NO .meta — entity-only carry .meta({ id })) ─────────────────

export const ListReviewsQuery = z.object({
  status: ReviewStatus.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListReviewsQuery = z.infer<typeof ListReviewsQuery>;

/** The operator list response: the reviews page + the salon's aggregate (drives the moderation header). */
export const ReviewList = z.object({
  data: z.array(Review),
  averageRating: z.number().min(0).max(5),
  reviewCount: z.int().min(0),
  pagination: z.object({ limit: z.int(), offset: z.int(), total: z.int() }),
});
export type ReviewList = z.infer<typeof ReviewList>;
